import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { resetCacheStamp } from "../../util/cacheManager";
import { CacheStoreKey } from "../../enum/cacheStore.enum";
import { Model } from "../../types/Model";
import { handleNetworkResponse } from "../../util/helper";
import { Persister } from "../../util/persister";

// Define a service using a base URL and expected endpoints
export const modelApi = createApi({
  reducerPath: "modelApi",
  tagTypes: [CacheStoreKey.metaModelDBStamp],
  baseQuery: fetchBaseQuery({ baseUrl: "" }),
  endpoints: (build) => ({
    // mutation api
    createModel: build.mutation({
      queryFn: async (model: Omit<Model, 'id'>) => {
        resetCacheStamp(CacheStoreKey.metaModelDBStamp);
        const response = await axios.post(
          `/model/create`,
          model
        );
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaModelDBStamp],
    }),

    updateModel: build.mutation({
      queryFn: async ( model: Model ) => {
        resetCacheStamp(CacheStoreKey.metaModelDBStamp);
        const response = await axios.post(
          `/model/update`,
          model
        );
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaModelDBStamp],
    }),

    deleteModel: build.mutation({
      queryFn: async (args: { id: number }) => {
        resetCacheStamp(CacheStoreKey.metaModelDBStamp);
        const response = await axios.post(`/model/delete/${args.id}`);
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.metaModelDBStamp],
    }),

    // read api
    readModels: build.query({
      queryFn: async (args: {}) => {
        const response = await Persister.requestData(CacheStoreKey.metaModelDBStamp, 'GET', `/models`);
        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.metaModelDBStamp],
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useCreateModelMutation,
  useDeleteModelMutation,
  useUpdateModelMutation,
  useReadModelsQuery,
  useLazyReadModelsQuery
} = modelApi;

export type ModelApiType = typeof modelApi;
